import { Button, Divider, Steps } from 'antd';
import ModalCustom from 'components/commons/ModalCustom';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import UploadFileButton from './UploadFileButton';
import DataImportContent from './DataImportContent';
import ButtonBath from './ButtonBath';

const StepWrapper = styled.div`
  .content-data {
    padding: 20px;
    text-align: center;
    border-radius: 4px;
    &__success {
      background-color: rgba(102, 193, 31, 0.1);
      color: #66c11f;
    }
    &__fail {
      background-color: rgba(171, 35, 40, 0.2);
      color: #ab2328;
    }
    &__loading {
      background-color: rgba(182, 184, 193, 0.2);
      color: var(--text-tab-title);
    }
  }
  .ant-table-thead th {
    text-align: center !important;
    font-weight: 700 !important;
    color: var(--text-title-gray) !important;
    background-color: var(--border-color-light) !important;
  }
  .ant-divider-horizontal {
    margin: 20px 0 !important;
  }
`;

interface IImportModalProps {
  title?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  importActions?: any;
  source?: string;
  extraContent?: React.ReactNode;
  // eslint-disable-next-line
  extraRef?: any;
}

const ImportModal = (
  { title, importActions, source, extraContent, extraRef }: IImportModalProps,
  ref,
) => {
  const { t } = useTranslation();

  const uploadRef = useRef(null);

  const [visibleModal, setVisibleModal] = useState(false);
  const [current, setCurrent] = useState(0);
  const [loadingImport, setLoadingImport] = useState<boolean>(false);
  const [jobId, setJobId] = useState<string>('');
  const [disabled, setDisabled] = useState<boolean>(true);

  useImperativeHandle(
    ref,
    () => ({
      openModal: () => setVisibleModal(true),
    }),
    [],
  );

  const handleCancel = () => {
    setVisibleModal(false);
    setCurrent(0);
    setJobId('');
    setDisabled(true);
  };

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const steps = [
    {
      title: (
        <span className="text-sm fw-500 line-18">{t('button.uploadFile')}</span>
      ),
      content: (
        <UploadFileButton
          ref={uploadRef}
          next={next}
          setLoadingImport={setLoadingImport}
          setJobId={setJobId}
          setDisabled={setDisabled}
          importActions={importActions}
          source={source}
          extraContent={extraContent}
          extraRef={extraRef}
        />
      ),
    },
    {
      title: (
        <div className="text-sm fw-500 line-18">
          {t('locationStocks.matchSpreadsheet')}
        </div>
      ),
      content: (
        <div className="text-sm fw-400">
          <div className="text-title-gray">
            {t('locationStocks.importSuccessTitle')}
          </div>
          <div>{t('locationStocks.importSuccessDescription')}</div>
        </div>
      ),
    },
    {
      title: (
        <div className="text-sm fw-500 line-18">
          {t('locationStocks.validateData')}
        </div>
      ),
      content: <DataImportContent jobId={jobId} setDisabled={setDisabled} />,
    },
  ];

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  return (
    <ModalCustom
      open={visibleModal}
      onCancel={handleCancel}
      title={t(title || 'locationStocks.import')}
      width={700}
      footer={null}
    >
      <StepWrapper>
        <Steps current={current} items={items} labelPlacement="vertical" />
        <Divider />
        <div>{steps[current].content}</div>
        <div style={{ marginTop: 20 }} className="flex-end d-flex">
          {current === 0 && (
            <Button
              type="primary"
              onClick={() => {
                uploadRef.current.import();
              }}
              loading={loadingImport}
              disabled={disabled}
            >
              {t('button.import')}
            </Button>
          )}
          {current > 0 && (
            <Button
              style={{ margin: '0 8px' }}
              onClick={() => {
                prev();
                setDisabled(true);
              }}
            >
              {t('button.back')}
            </Button>
          )}
          {current < steps.length - 1 && current !== 0 && (
            <ButtonBath onClick={() => next()} />
          )}
          {current === steps.length - 1 && (
            <Button type="primary" onClick={handleCancel} disabled={disabled}>
              Done
            </Button>
          )}
        </div>
      </StepWrapper>
    </ModalCustom>
  );
};

export default forwardRef(ImportModal);
